import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";

import wacToken from "../../../../libraries/design/foundations/token/src/wac-token";

import { Burst } from "./assets/burst";
import { IntelligenceDark, IntelligenceLight } from "./assets/intelligence";

type AiSectionProps = {
    readonly title: string;
    readonly videoLink: string;
    readonly theme: string;
    readonly includeFooter: boolean;
    readonly buttonPosition?: string;
    readonly button: React.ReactNode;
    readonly videoContainerClassName?: string;
    readonly videoClassName?: string;
    readonly showBurst: boolean;
    readonly burstClassName1?: string;
    readonly burstClassName2?: string;
};

const aiParentStyles = css({
    width: "100%"
});

const videoStyles = css({
    maxWidth: "1187px",
    height: "auto",
    padding: "16px",
    [media.below.lg]: {
        display: "none"
    }
});

const imageStyles = css({
    width: "100%",
    height: "auto",
    padding: "16px 25px 0px 25px",
    [media.above.lg]: {
        display: "none"
    }
});

const footerStyles = css({
    width: "100%",
    zIndex: "-1",
    marginBlockStart: "-1px"
});

export const Ai = ({
    theme,
    title,
    buttonPosition,
    button,
    videoLink,
    includeFooter,
    showBurst,
    videoContainerClassName,
    videoClassName,
    burstClassName1,
    burstClassName2
}: AiSectionProps) => {
    const intl = useIntl();

    const aiSectionStyles = css({
        "display": "flex",
        "flexDirection": "column",
        "alignItems": "center",
        "backgroundColor": theme === "light" ? "#CFE1FD" : "#1868DB",
        "color": "white",
        "height": "auto",
        "overflow": "hidden",
        "padding": "28px 10% 0 10%",
        "flexShrink": "0",
        "width": "100%",
        "position": "relative",
        // Tech debt: Adding this disable to unblock commits for GWP-2646
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ".hide": {
            display: "none"
        },
        [media.below.md]: {
            padding: "40px 0 0 0"
        }
    });

    const buttonSectionStyles = css({
        "display": "flex",
        "flexDirection": "column",
        "alignItems": "center",
        "marginTop": "12px",
        // Tech debt: Adding this disable to unblock commits for GWP-2646
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ".hide": {
            display: "none"
        }
    });

    const titleStyles = css({
        color: theme === "light" ? "#101214" : "#FFF",
        textAlign: "center",
        fontFeatureSettings: "'clig' off, 'liga' off",
        fontFamily: wacToken("font.charlie.display"),
        fontSize: "48px",
        fontStyle: "normal",
        fontWeight: "700",
        lineHeight: "48px",
        paddingBlockEnd: "32px",
        [media.only.md]: {
            fontSize: "32px"
        },
        [media.only.sm]: {
            fontSize: "32px"
        },
        [media.below.sm]: {
            fontSize: "24px"
        }
    });

    const burstStyles1 = css({
        position: "absolute",
        height: "98px",
        width: "100px",
        insetInlineStart: "10%",
        [media.below.lg]: {
            display: "none"
        }
    });

    const burstStyles2 = css({
        position: "absolute",
        height: "73px",
        width: "75px",
        insetBlockEnd: "20px",
        insetInlineEnd: "10%",
        [media.below.lg]: {
            display: "none"
        }
    });

    const videoContainerStyles = css({
        height: "auto",
        [media.below.lg]: {
            display: "none"
        }
    });

    return (
        <div css={aiParentStyles}>
            <div css={aiSectionStyles}>
                <div css={burstStyles1} className={burstClassName1}>
                    {showBurst && <Burst />}
                </div>
                {theme === "light" ? (
                    <IntelligenceLight />
                ) : (
                    <IntelligenceDark />
                )}
                <h2 css={titleStyles}>{title}</h2>
                <div
                    css={buttonSectionStyles}
                    className={buttonPosition !== "bottom" ? "" : "hide"}
                >
                    {button}
                </div>
                <div css={burstStyles2} className={burstClassName2}>
                    {showBurst && <Burst />}
                </div>
                <div
                    css={videoContainerStyles}
                    className={videoContainerClassName}
                >
                    <video
                        css={videoStyles}
                        playsInline={true}
                        muted={true}
                        preload="auto"
                        autoPlay={true}
                        loop={true}
                        className={videoClassName}
                    >
                        {intl.formatMessage({
                            id: "ConfluenceSoftwarePage.v4.AISection.imgAlt",
                            defaultMessage: "demo of AI integration"
                        })}
                        <source src={videoLink} type={`video/webm`} />
                    </video>
                </div>
                {/* eslint-disable-next-line no-restricted-syntax */}
                <img
                    css={imageStyles}
                    src="https://wac-cdn.atlassian.com/misc-assets/webp-images/ai-img.webp"
                    loading="lazy"
                    alt={intl.formatMessage({
                        id: "ConfluenceSoftwarePage.v4.AISection.imgAlt",
                        defaultMessage: "demo of AI integration"
                    })}
                />
            </div>
            {includeFooter && (
                // eslint-disable-next-line no-restricted-syntax
                <img
                    css={footerStyles}
                    src="https://wac-cdn.atlassian.com/misc-assets/webp-images/blue-angle.webp"
                    alt=""
                />
            )}
            <div
                css={buttonSectionStyles}
                className={buttonPosition === "bottom" ? "" : "hide"}
            >
                {button}
            </div>
        </div>
    );
};
